/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react';
import {ColumnConfig} from '../../layout/table/columnConfig';
import {GuestComment} from './guestComment';
import {sortByDateProp, sortByProp} from '../../language/module';
import {ApiError} from '../../core/redux/api/apiError';
import {COLUMN_SPACING} from '../../forms/styles';
import LabeledData from '../../forms/elements/LabeledData';
import Table from '../../layout/table/Table';
import {apostrophize} from '../../language/strings';
import {formatDate} from '../../language/time';
import { getResource } from '../../language/resources/getResource';
import { IAppState } from '../../core/redux/IAppState';
import { useSelector } from 'react-redux';


const allColumns     = css`word-wrap: break-word; hyphens: auto;`;
const dateColumn     = css`width: 105px; ${allColumns};`;
const agentColumn    = css`max-width: 100px; ${allColumns};`;
const subjectColumn  = css`max-width: 230px; ${allColumns};`;
const commentsColumn = css`white-space: pre-line; ${allColumns}; max-width: 600px;`;


type Props = {
  guestName: string;
  commentHistory: GuestComment[] | undefined;
  isLoadingCommentHistory: boolean;
  commentHistoryLoadError: ApiError | undefined;
}

export const CommentHistoryView = (props: Props) => {
  const resources = useSelector((state: IAppState) => state.locale);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);

  const columnConfig: Array<ColumnConfig<GuestComment>> = [
    { id: 1, title: getResource(resources, 'COMMENT_HISTORY_COLUMN_DATE'), onSort: sortByDateProp('updateDate'), defaultSortingColumn: true, defaultSortingDirection: 'desc' },
    { id: 2, title: getResource(resources, 'COMMENT_HISTORY_COLUMN_AGENT'), onSort: sortByProp('operatorId', true) },
    { id: 3, title: getResource(resources, 'COMMENT_HISTORY_COLUMN_SUBJECT'), onSort: sortByProp('subjectText', true) },
    { id: 4, title: getResource(resources, 'COMMENT_HISTORY_COLUMN_COMMENT'), onSort: sortByProp('commentText', true) },
  ];

  const renderRow = (history: GuestComment, index: number): React.ReactNode => (
    <tr key={index} data-testid='comments-history-row'>
      <td css={dateColumn} data-testid='comments-history-row-date-status'>{formatDate(history.updateDate, dateFormat)}</td>
      <td css={agentColumn} data-testid='comments-history-row-agent-status'>{history.operatorId}</td>
      <td css={subjectColumn} data-testid='comments-history-row-subject-status'>{history.subjectText}</td>
      <td css={commentsColumn} data-testid='comments-history-row-text-status'>{history.commentText}</td>
    </tr>
  );

  return (
    <div css={COLUMN_SPACING}>
      {/* Guest Name */}
      <LabeledData label={getResource(resources, 'ADD_COMMENT_GUEST_LABEL')} data={props.guestName}/>

      <Table columns={columnConfig}
             data={props.commentHistory!}
             isLoading={props.isLoadingCommentHistory!}
             noDataMessage={getResource(resources, 'COMMENT_HISTORY_NO_HISTORY')}
             error={props.commentHistoryLoadError
               ? getResource(resources, 'COMMENT_HISTORY_FAILED_FETCH', apostrophize(props.guestName))
               : undefined
             }
             renderRow={renderRow}/>
    </div>
  );

}
